import type {
  RoadSpeedLimitSettingsAPI,
  SettingsJsonAPI,
  StopIdleTimeSettingsAPI,
  UserAlertSettingsAPI,
  VehicleSpeedLimitSettingsAPI,
} from './definitions/ApiTypes';
import type {
  RoadSpeedLimitSettings,
  SettingsJson,
  StopIdleTimeSettings,
  UserAlertSettings,
  VehicleSpeedLimitSettings,
} from './definitions/Types';

import { SettingsType, toSeverity, toSeverityAPI } from '../commons';

const isRoadSpeedLimitJson = (
  settingsJson: SettingsJsonAPI
): settingsJson is RoadSpeedLimitSettingsAPI => {
  return (
    (settingsJson as RoadSpeedLimitSettingsAPI)
      .duration_above_limit_in_millis !== undefined
  );
};

const isVehicleSpeedLimitJson = (
  settingsJson: SettingsJsonAPI
): settingsJson is VehicleSpeedLimitSettingsAPI => {
  return (
    (settingsJson as VehicleSpeedLimitSettingsAPI)
      .duration_above_limit_in_millis !== undefined
  );
};

const isStopIdleTimeJson = (
  settingsJson: SettingsJsonAPI
): settingsJson is StopIdleTimeSettingsAPI => {
  return (
    (settingsJson as StopIdleTimeSettingsAPI).duration_idle_in_millis !==
    undefined
  );
};

const toSettingsJson = (
  userAlertSettingsAPI: UserAlertSettingsAPI
): SettingsJson | undefined => {
  const { settings_json: settings } = userAlertSettingsAPI;

  if (settings) {
    switch (userAlertSettingsAPI.type) {
      case SettingsType.ROAD_SPEED_LIMIT:
        if (isRoadSpeedLimitJson(settings)) {
          return {
            durationAboveLimitInMillis: settings.duration_above_limit_in_millis,
            severity: toSeverity(settings.severity),
            vehicleGroupIds: settings.vehicle_group_ids,
          };
        }
        break;

      case SettingsType.VEHICLE_SPEED_LIMIT:
        if (isVehicleSpeedLimitJson(settings)) {
          return {
            durationAboveLimitInMillis: settings.duration_above_limit_in_millis,
            vehicleGroupIds: settings.vehicle_group_ids,
          };
        }
        break;

      case SettingsType.STOP_IDLE_TIME:
        if (isStopIdleTimeJson(settings)) {
          return {
            durationIdleInMillis: settings.duration_idle_in_millis,
            vehicleGroupIds: settings.vehicle_group_ids,
          };
        }
        break;
    }
  }
};

export const toUserAlertSettings = (
  settingsAPI: UserAlertSettingsAPI
): UserAlertSettings => ({
  id: settingsAPI.id,
  type: settingsAPI.type,
  active: settingsAPI.active,
  settingsJson: toSettingsJson(settingsAPI),
});

// API

const isRoadSpeedLimitJsonAPI = (
  settingsJson: SettingsJson
): settingsJson is RoadSpeedLimitSettings => {
  return (
    (settingsJson as RoadSpeedLimitSettings).durationAboveLimitInMillis !==
    undefined
  );
};

const isVehicleSpeedLimitJsonAPI = (
  settingsJson: SettingsJson
): settingsJson is VehicleSpeedLimitSettings => {
  return (
    (settingsJson as VehicleSpeedLimitSettings).durationAboveLimitInMillis !==
    undefined
  );
};

const isStopIdleTimeJsonAPI = (
  settingsJson: SettingsJson
): settingsJson is StopIdleTimeSettings => {
  return (
    (settingsJson as StopIdleTimeSettings).durationIdleInMillis !== undefined
  );
};

const toSettingsJsonAPI = (
  userAlertSettings: UserAlertSettings
): SettingsJsonAPI | undefined => {
  const { settingsJson } = userAlertSettings;

  if (settingsJson) {
    switch (userAlertSettings.type) {
      case SettingsType.ROAD_SPEED_LIMIT:
        if (settingsJson && isRoadSpeedLimitJsonAPI(settingsJson)) {
          return {
            duration_above_limit_in_millis:
              settingsJson.durationAboveLimitInMillis,
            severity: toSeverityAPI(settingsJson.severity),
            vehicle_group_ids: settingsJson.vehicleGroupIds,
          };
        }
        break;

      case SettingsType.VEHICLE_SPEED_LIMIT:
        if (settingsJson && isVehicleSpeedLimitJsonAPI(settingsJson)) {
          return {
            duration_above_limit_in_millis:
              settingsJson.durationAboveLimitInMillis,
            vehicle_group_ids: settingsJson.vehicleGroupIds,
          };
        }
        break;

      case SettingsType.STOP_IDLE_TIME:
        if (settingsJson && isStopIdleTimeJsonAPI(settingsJson)) {
          return {
            duration_idle_in_millis: settingsJson.durationIdleInMillis,
            vehicle_group_ids: settingsJson.vehicleGroupIds,
          };
        }
        break;
    }
  }
};

export const toUserAlertSettingsAPI = (
  settings: UserAlertSettings
): UserAlertSettingsAPI => ({
  id: settings.id,
  type: settings.type,
  active: settings.active,
  settings_json: toSettingsJsonAPI(settings),
});
